/**
 * Matches backend `computeBookingAmounts` / public Book.jsx payment rules.
 *
 * - Departure within 40 days: full total payable now (advance = total).
 * - Departure more than 40 days away and per-person share > ₹5,000: ₹5,000 advance per person.
 * - Departure more than 40 days away and per-person share ≤ ₹5,000: full per-person share now.
 * - Remaining balance due 20 days before departure.
 */

export const ADVANCE_CAP = 5000;
export const ADVANCE_WINDOW_DAYS = 40;
export const REMAINING_DUE_DAYS_BEFORE_DEPARTURE = 20;

function bookingParticipantCount(booking) {
  return Math.max(
    Number(booking?.travelerCount) || 0,
    Array.isArray(booking?.travelers) ? booking.travelers.length : 0,
    1
  );
}

export function daysUntilBatchStart(batchStartDate) {
  if (!batchStartDate) return null;
  const raw = String(batchStartDate).trim();
  const start = raw.length >= 10 ? new Date(`${raw.slice(0, 10)}T12:00:00`) : new Date(raw);
  if (Number.isNaN(start.getTime())) return null;
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const departureStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  return Math.ceil((departureStart - todayStart) / (24 * 60 * 60 * 1000));
}

export function computePolicyAdvanceAmounts(totalAmount, batchStartDate, travelerCount = 1) {
  const total = Number(totalAmount ?? 0);
  const n = Math.max(1, Number(travelerCount) || 1);
  if (total <= 0) {
    return { advanceAmount: 0, remainingAmount: 0, isWithinAdvanceWindow: false };
  }
  const perPersonShare = total / n;
  const daysUntil = daysUntilBatchStart(batchStartDate);
  const isWithinAdvanceWindow = daysUntil !== null && daysUntil <= ADVANCE_WINDOW_DAYS;
  const perPersonAdvance = isWithinAdvanceWindow
    ? perPersonShare
    : perPersonShare > ADVANCE_CAP
      ? ADVANCE_CAP
      : perPersonShare;
  const advanceAmount = isWithinAdvanceWindow ? total : perPersonAdvance * n;
  return {
    advanceAmount,
    remainingAmount: Math.max(0, total - advanceAmount),
    isWithinAdvanceWindow,
    daysUntilDeparture: daysUntil,
    perPersonAdvance
  };
}

/**
 * Prefer stored `advanceAmount` when it matches policy; otherwise derive from batch start + headcount.
 * Legacy rows stored a single ₹5k cap for the whole group — re-derive per-person advance for display/Pay.
 */
export function effectiveBookingAdvance(booking) {
  const total = Number(booking?.totalAmount ?? 0);
  const count = bookingParticipantCount(booking);
  const batchStart = booking?.batch?.startDate;
  const policy = computePolicyAdvanceAmounts(total, batchStart, count);
  const stored = Number(booking?.advanceAmount ?? 0);
  if (stored > 0) {
    const aligned =
      Math.abs(stored - policy.advanceAmount) <= 1 ||
      (policy.isWithinAdvanceWindow && stored >= total && total > 0);
    if (aligned) {
      return {
        advanceAmount: stored,
        remainingAmount: Math.max(0, total - stored),
        isWithinAdvanceWindow: stored >= total && total > 0,
        fromStoredBooking: true
      };
    }
  }
  return { ...policy, fromStoredBooking: false };
}

/** Separate ₹5k-style advance vs pay-full-now (advance equals total). */
export function hasPartialAdvanceStructure(total, advanceAmount) {
  const t = Number(total ?? 0);
  const a = Number(advanceAmount ?? 0);
  return t > 0 && a > 0 && a < t;
}
